const pillars = [
  {
    number: "01",
    icon: "ri-search-eye-line",
    title: "Product Sourcing",
    anchorId: "product-sourcing",
    tagline: "Find the right product, from the right supplier, at the right price.",
    description:
      "LTF Sourcing handles every step of the supplier discovery process — from initial research to final approval. We vet, negotiate, and coordinate on your behalf so you save time and avoid costly sourcing mistakes.",
    bullets: [
      "Global supplier discovery & matching",
      "Price negotiation & quote comparison",
      "Sample coordination & product testing",
      "Factory audits & background checks",
    ],
    color: "bg-yellow-50",
    iconColor: "text-[#C9A227]",
    borderColor: "border-[#C9A227]",
    numberColor: "text-[#C9A227]/20",
    featured: false,
  },
  {
    number: "02",
    icon: "ri-store-3-line",
    title: "Wholesale Supply",
    anchorId: "wholesale-supply",
    tagline: "Bulk pricing. Direct connections. Growing category range.",
    description:
      "Access our verified network of wholesale distributors and manufacturers offering competitive bulk rates. Whether you're replenishing stock or scaling up a new product line, we connect you directly — no middlemen, no markup.",
    bullets: [
      "Bulk pricing across 40+ product categories",
      "Direct manufacturer & distributor access",
      "Multi-supplier order consolidation",
      "Flexible minimum order quantities",
    ],
    color: "bg-[#D9E2EC]",
    iconColor: "text-[#3A506B]",
    borderColor: "border-[#3A506B]/30",
    numberColor: "text-[#3A506B]/15",
    featured: false,
  },
  {
    number: "03",
    icon: "ri-archive-drawer-line",
    title: "Inventory Solutions",
    anchorId: "inventory-solutions",
    tagline: "Turn dead stock into dollars. Find clearance deals instantly.",
    description:
      "Whether you need to offload surplus inventory or find deep-discount bulk merchandise, our overstock marketplace connects buyers and sellers of liquidation, closeout, and excess stock — at prices that move fast.",
    bullets: [
      "Overstock & liquidation marketplace",
      "Bulk buyer network for surplus goods",
      "Custom quote requests by category",
      "Seller portal for listing your inventory",
    ],
    color: "bg-[#C9A227]/10",
    iconColor: "text-[#C9A227]",
    borderColor: "border-[#C9A227]",
    numberColor: "text-[#C9A227]/20",
    featured: true,
  },
];

const supporting = [
  {
    icon: "ri-shield-check-line",
    title: "Quality Inspection",
    description: "On-site and remote QC services to ensure products meet your exact specs before shipment.",
    iconColor: "text-teal-600",
    bg: "bg-teal-50",
  },
  {
    icon: "ri-truck-line",
    title: "Logistics Coordination",
    description: "Sea, air & land freight with full customs clearance and real-time shipment tracking.",
    iconColor: "text-[#3A506B]",
    bg: "bg-[#D9E2EC]",
  },
  {
    icon: "ri-line-chart-line",
    title: "Procurement Strategy",
    description: "Spend analytics, vendor consolidation, and process consulting to cut costs at scale.",
    iconColor: "text-[#C9A227]",
    bg: "bg-yellow-50",
  },
];

export default function ServicesSection() {
  return (
    <section id="services" className="py-24 bg-[#D9E2EC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Header */}
        <div className="flex flex-col items-center text-center mb-16">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#C9A227] mb-3">
            What We Do
          </span>
          <h2 className="text-4xl lg:text-5xl font-extrabold text-[#222831] leading-tight max-w-3xl">
            Product Sourcing, Wholesale Supply &amp; Inventory Solutions
          </h2>
          <p className="mt-4 text-base text-[#3A506B] max-w-xl leading-relaxed">
            LTF Sourcing is built around three core <strong>procurement</strong> capabilities — <strong>product sourcing</strong> from verified global suppliers, <strong>wholesale supply</strong> across 40+ categories, and <strong>inventory solutions</strong> for surplus and overstock goods.
          </p>
        </div>

        {/* Three Pillars */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {pillars.map((pillar) => (
            <div
              key={pillar.title}
              id={pillar.anchorId}
              className={`relative rounded-2xl bg-white p-8 flex flex-col gap-5 border-2 ${pillar.borderColor} hover:-translate-y-1 transition-transform duration-300 overflow-hidden`}
            >
              {/* Background number watermark */}
              <span className={`absolute top-4 right-5 text-8xl font-extrabold leading-none select-none pointer-events-none ${pillar.numberColor}`}>
                {pillar.number}
              </span>

              {pillar.featured && (
                <div className="absolute top-0 left-0 right-0 h-1 bg-[#C9A227] rounded-t-2xl"></div>
              )}

              {/* Icon */}
              <div className={`w-14 h-14 flex items-center justify-center rounded-2xl ${pillar.color} relative z-10`}>
                <i className={`${pillar.icon} text-3xl ${pillar.iconColor}`}></i>
              </div>

              {/* Text */}
              <div className="relative z-10">
                <p className={`text-xs font-bold uppercase tracking-widest mb-1 ${pillar.iconColor}`}>
                  Pillar {pillar.number}
                </p>
                <h3 className="text-2xl font-extrabold text-[#222831] leading-tight mb-2">
                  {pillar.title}
                </h3>
                <p className={`text-sm font-semibold italic mb-3 ${pillar.iconColor}`}>
                  {pillar.tagline}
                </p>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {pillar.description}
                </p>
              </div>

              {/* Bullets */}
              <ul className="flex flex-col gap-2 relative z-10">
                {pillar.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm text-[#3A506B]">
                    <i className={`ri-check-line text-base mt-0.5 flex-shrink-0 ${pillar.iconColor}`}></i>
                    {b}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button className={`mt-auto flex items-center gap-1.5 text-sm font-bold hover:gap-2.5 transition-all cursor-pointer whitespace-nowrap ${pillar.iconColor}`}>
                Learn More <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          ))}
        </div>

        {/* Supporting Services Strip */}
        <div className="rounded-2xl bg-white border border-[#D9E2EC] overflow-hidden">
          <div className="px-8 py-5 border-b border-[#D9E2EC]">
            <p className="text-xs font-bold uppercase tracking-widest text-[#3A506B]">
              Also Included — Supporting Services
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#D9E2EC]">
            {supporting.map((s) => (
              <div key={s.title} className="flex items-start gap-4 px-8 py-6">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${s.bg} flex-shrink-0 mt-0.5`}>
                  <i className={`${s.icon} text-xl ${s.iconColor}`}></i>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-[#222831] mb-1">{s.title}</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">{s.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
